/**
 * Bug-report link helper — issue #969.
 *
 * Reads VITE_BUG_REPORT_REPO / VITE_BUG_REPORT_DISABLED from the build
 * environment so forks and white-label / OEM deployments can point at
 * their own tracker or hide the link entirely without a code change.
 *
 * No client-side context capture — the link is a plain URL with no
 * pre-fill, no `window.location.href`, no project id, no version
 * string. The GitHub issue form scaffolds what we need from the user.
 */

/**
 * The PUBLIC issue tracker — deliberately not the code repo.
 *
 * `mudit70/veodiagram` is private, and GitHub answers 404 (not 403) for
 * repos the viewer can't see, so pointing the header link there gave every
 * end user a 404 while working fine for maintainers — the link only
 * resolved for accounts with read access. `veodiagram-issues` is public and
 * carries the same bug.yml / feature.yml templates, so `/issues/new/choose`
 * renders for anyone; unauthenticated visitors get GitHub's sign-in
 * redirect with `return_to` preserved, which is the intended flow.
 */
const DEFAULT_REPO = 'mudit70/veodiagram-issues';

export function getBugReportConfig(): {
  enabled: boolean;
  href: string;
} {
  // `||`, deliberately, NOT `??`. In the Docker build `apps/web/Dockerfile`
  // declares `ARG VITE_BUG_REPORT_REPO` and then `ENV VITE_BUG_REPORT_REPO=$ARG`.
  // When the arg isn't passed — which is every deploy, since deploy.sh only
  // passes VITE_API_URL — Docker sets the ENV to an EMPTY STRING rather than
  // leaving it unset. `??` only falls back on null/undefined, so the empty
  // string won, producing `https://github.com//issues/new/choose` (note the
  // double slash) and a 404 for every user of every container build. The
  // helper default was never reached in production.
  const configured = (import.meta.env.VITE_BUG_REPORT_REPO as string | undefined)?.trim();
  const repo = configured || DEFAULT_REPO;
  const disabled =
    (import.meta.env.VITE_BUG_REPORT_DISABLED as string | undefined) === 'true';
  return {
    enabled: !disabled,
    // /choose so the user picks bug vs feature; falls through to the
    // generic new-issue page on repos that don't have templates.
    href: `https://github.com/${repo}/issues/new/choose`,
  };
}
